import { Button } from "components/Button/Button";
import { Callout } from "components/Form/components/Callout/Callout";
import { Form } from "components/Form/components/Form/Form";
import { FormComponentProps } from "components/Form/components/FormComponentProps";
import { Password } from "components/Form/components/Password/Password";
import { SimpleToggle } from "components/Form/components/Switch/Switch";
import { SwitchFormComponent } from "components/Form/components/Switch/SwitchFormComponent";
import { Input } from "components/Form/components/Input/Input";
import { IconCollection } from "components/Icon/IconCollection";
import { useEffect, useRef, useState } from "react";
import { Field } from "react-final-form";
import { v4 as uuidv4 } from "uuid";
import "./WebhookSecurity.scss";

const RequireKeyField = (props: FormComponentProps) => {
    const {
        input: {
            onChange,
            value: { is_key_required, key },
        },
    } = props;

    const [isFieldOpened, setFieldOpened] = useState(false);
    const [hasKeyInitially] = useState(is_key_required);

    const inputRef = useRef<any>();

    const changeToInputField = () => {
        setFieldOpened(true);
    };

    useEffect(() => {
        if (isFieldOpened && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isFieldOpened]);

    const label = hasKeyInitially ? "Change key" : "Set key";

    useEffect(() => {
        if (is_key_required === false) {
            setFieldOpened(false);
        }
    }, [is_key_required]);

    useEffect(() => {
        const currentRef = inputRef.current;
        if (currentRef) {
            const removeReadOnly = () => currentRef.removeAttribute("readonly");
            const setReadOnly = () => (currentRef.readOnly = true);
            currentRef.addEventListener("mousedown", removeReadOnly);
            currentRef.addEventListener("mouseout", setReadOnly);

            return () => {
                currentRef.removeEventListener("mousedown", removeReadOnly);
                currentRef.removeEventListener("mouseout", setReadOnly);
            };
        }
    });

    return (
        <div className="requireKeyContainer">
            <SwitchFormComponent
                {...props}
                input={{
                    name: "is_key_required",
                    id: "is_key_required",
                    value: is_key_required,
                    onChange: (changedValue) =>
                        onChange({
                            key: changedValue === false ? "" : key,
                            is_key_required: changedValue,
                        }),
                    onBlur: () => {},
                    onFocus: () => {},
                }}
                fieldProps={{
                    label: "Key required",
                    docs: {
                        text: "Key to authenticate the webhook call",
                        urlPath: "/docs/webhooks#configuration",
                    },
                }}
            />
            {is_key_required === true ? (
                <div className="setKeyContainer">
                    {isFieldOpened ? (
                        <>
                            <div className="generateKey">
                                <div className="keyContainer">
                                    <div>Please choose a key:</div>
                                    <Password
                                        input={{
                                            value: key,
                                            onChange: (e) =>
                                                onChange({ is_key_required, key: e.target.value }),
                                        }}
                                        attributes={{}}
                                        name="key"
                                        touched={false}
                                        errorMessage={""}
                                        preventAutofill
                                        inputRef={inputRef}
                                    />
                                </div>
                                <Button
                                    onClick={() => onChange({ is_key_required, key: uuidv4() })}
                                    icon={IconCollection.key}
                                    variation="dark"
                                    label={"Generate key"}
                                />
                            </div>
                            <Callout
                                text="INFO: Make sure to store your key safely. You will not be able to see it again after it has been set."
                                type="info"
                            />
                        </>
                    ) : (
                        <Button
                            onClick={changeToInputField}
                            icon={hasKeyInitially ? IconCollection.autorenew : IconCollection.key}
                            variation="dark"
                            label={label}
                        />
                    )}
                    {props.meta.submitError && (
                        <span className="input__helper-text input__helper-text--error">
                            {props.meta.submitError}
                        </span>
                    )}
                </div>
            ) : null}
        </div>
    );
};

export interface WebhookSecurityDisplayValues {
    is_enabled: boolean;
    is_key_required: boolean;
    require_login: boolean;
}

const ReadOnlySecuritySwitch = ({
    identifier,
    label,
    value,
    docs,
}: {
    identifier: string;
    label: string;
    value: boolean;
    docs: { text: string; urlPath: string };
}) => (
    <Form.Row>
        <Input
            fieldProps={{ label, docs }}
            fieldAttr={{ disabled: true }}
            input={{ name: identifier, value, onChange: () => {}, onBlur: () => {}, onFocus: () => {} }}
            meta={{}}
        >
            <SimpleToggle identifier={identifier} value={value} isReadOnly />
        </Input>
    </Form.Row>
);

export const WebhookSecurityInnerForm = ({
    isReadOnly,
    displayValues,
}: {
    isReadOnly: boolean;
    displayValues?: WebhookSecurityDisplayValues;
}) => {
    if (isReadOnly && displayValues) {
        return (
            <>
                <ReadOnlySecuritySwitch
                    identifier="is_enabled"
                    label="Enabled"
                    value={displayValues.is_enabled}
                    docs={{
                        text: "Enable or disable this component",
                        urlPath: "/docs/webhooks#configuration",
                    }}
                />
                <ReadOnlySecuritySwitch
                    identifier="require_login"
                    label="Require login token"
                    value={displayValues.require_login}
                    docs={{
                        text: "Does the client have to provide a valid Cloudomation login token for the call",
                        urlPath: "/docs/webhooks#configuration",
                    }}
                />
                <ReadOnlySecuritySwitch
                    identifier="is_key_required"
                    label="Key required"
                    value={displayValues.is_key_required}
                    docs={{
                        text: "Key to authenticate the webhook call",
                        urlPath: "/docs/webhooks#configuration",
                    }}
                />
            </>
        );
    }

    return (
        <>
            <Form.Row>
                <Field
                    name="is_enabled"
                    id="is_enabled"
                    fieldProps={{
                        label: "Enabled",
                        docs: {
                            text: "Enable or disable this component",
                            urlPath: "/docs/webhooks#configuration",
                        },
                    }}
                    fieldAttr={{
                        disabled: isReadOnly,
                    }}
                    component={SwitchFormComponent}
                />
            </Form.Row>
            <Form.Row>
                <Field
                    name="require_login"
                    id="require_login"
                    fieldProps={{
                        label: "Require login token",
                        docs: {
                            text: "Does the client have to provide a valid Cloudomation login token for the call",
                            urlPath: "/docs/webhooks#configuration",
                        },
                    }}
                    fieldAttr={{
                        disabled: isReadOnly,
                    }}
                    component={SwitchFormComponent}
                />
            </Form.Row>
            <Form.Row>
                {isReadOnly ? (
                    <Field
                        name="is_key_required"
                        id="is_key_required"
                        fieldProps={{
                            label: "Key required",
                            docs: {
                                text: "Key to authenticate the webhook call",
                                urlPath: "/docs/webhooks#configuration",
                            },
                        }}
                        fieldAttr={{
                            disabled: isReadOnly,
                        }}
                        component={SwitchFormComponent}
                    />
                ) : (
                    <Field name="require_key_field" id="require_key_field" fieldProps={{}}>
                        {(props: FormComponentProps) => <RequireKeyField {...props} />}
                    </Field>
                )}
            </Form.Row>
        </>
    );
};
